import React from 'react';
import { XIcon } from 'lucide-react';
import {
  CHART_DASHBOARD_ARTIFACT_TYPE,
  isChartDashboardPayload,
  type ChartDashboardPayload,
} from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ChartView } from './chart-view';
import { ChartDashboardSparkPreview } from './chart-dashboard-spark-preview';
import {
  ChartDashboardProvider,
  useChartDashboard,
  useFilteredDashboardCharts,
} from './chart-dashboard-context';
import { chartBindsDimension } from './apply-dashboard-filters';

const DashboardFilterBar: React.FC = () => {
  const { payload, filters, clearDimension, clearFilters } =
    useChartDashboard();

  const activeEntries = Object.entries(filters).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  ) as Array<[string, string]>;

  if (activeEntries.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted-foreground">Filters</span>
      {activeEntries.map(([dimension, value]) => {
        const label = payload.dimensions[dimension]?.label ?? dimension;
        return (
          <Button
            key={dimension}
            type="button"
            variant="secondary"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => clearDimension(dimension)}
          >
            <span>
              {label}: {value}
            </span>
            <XIcon className="size-3 opacity-70" />
          </Button>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={clearFilters}
      >
        Clear all
      </Button>
    </div>
  );
};

const DashboardChartTile: React.FC<{
  chartIndex: number;
}> = ({ chartIndex }) => {
  const { payload, toggleFilter } = useChartDashboard();
  const filteredCharts = useFilteredDashboardCharts();
  const chartSpec = payload.charts[chartIndex];
  const filteredPayload = filteredCharts[chartIndex];

  if (chartSpec === undefined || filteredPayload === undefined) {
    return null;
  }

  const xDimension =
    chartSpec.bindings.x !== null &&
    chartSpec.bindings.x !== undefined &&
    chartSpec.bindings.x !== ''
      ? chartSpec.bindings.x
      : null;
  const seriesDimension =
    chartSpec.bindings.series !== null &&
    chartSpec.bindings.series !== undefined &&
    chartSpec.bindings.series !== ''
      ? chartSpec.bindings.series
      : null;

  return (
    <div className="min-w-0 rounded-lg border border-border bg-background p-3 shadow-xs">
      <h3 className="mb-2 truncate text-sm font-medium text-foreground">
        {chartSpec.title}
      </h3>
      {(xDimension !== null || seriesDimension !== null) && (
        <p className="mb-2 text-xs text-muted-foreground">
          Click{' '}
          {xDimension !== null && seriesDimension !== null
            ? 'bars or legend'
            : xDimension !== null
            ? 'bars'
            : 'legend'}{' '}
          to filter linked charts
        </p>
      )}
      <ChartView
        payload={filteredPayload}
        height={220}
        animate={false}
        showTitle={false}
        onCategoryClick={
          xDimension !== null
            ? (value) => toggleFilter(xDimension, value)
            : undefined
        }
        onSeriesClick={
          seriesDimension !== null
            ? (value) => toggleFilter(seriesDimension, value)
            : undefined
        }
      />
    </div>
  );
};

export const ChartDashboardView: React.FC<{
  payload: ChartDashboardPayload;
  className?: string;
}> = ({ payload, className }) => {
  const title =
    payload.title !== null && payload.title.trim() !== ''
      ? payload.title.trim()
      : 'Dashboard';

  return (
    <ChartDashboardProvider payload={payload}>
      <div
        className={cn(
          'flex h-full min-h-0 flex-col overflow-y-auto p-4',
          className
        )}
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">
            {payload.charts.length}{' '}
            {payload.charts.length === 1 ? 'chart' : 'charts'}
            {Object.keys(payload.dimensions).length > 0
              ? ' · linked filters'
              : ''}
          </p>
        </div>
        <DashboardFilterBar />
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
          {payload.charts.map((chart, index) => (
            <DashboardChartTile key={chart.chart_id} chartIndex={index} />
          ))}
        </div>
      </div>
    </ChartDashboardProvider>
  );
};

export const ChartDashboardSnippet: React.FC<{
  payload: ChartDashboardPayload;
  className?: string;
  onOpen?: () => void;
}> = ({ payload, className, onOpen }) => {
  const title =
    payload.title !== null && payload.title.trim() !== ''
      ? payload.title.trim()
      : 'Chart dashboard';
  const chartCount = payload.charts.length;
  const linkedDimensions = Object.keys(payload.dimensions).filter((key) =>
    payload.charts.some((chart) => chartBindsDimension(chart.bindings, key))
  );

  const subtitleParts = [
    `${chartCount} ${chartCount === 1 ? 'chart' : 'charts'}`,
  ];
  if (linkedDimensions.length > 0) {
    subtitleParts.push('cross-filtering');
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={onOpen === undefined}
      className={cn(
        'group flex w-full max-w-full flex-col gap-2 rounded-lg border border-input bg-background p-3 text-left shadow-xs transition-colors',
        onOpen !== undefined &&
          'cursor-pointer hover:bg-muted/60 hover:border-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        onOpen === undefined && 'cursor-default',
        className
      )}
      aria-label={onOpen !== undefined ? `Open ${title}` : title}
    >
      <div className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">
          {title}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {subtitleParts.join(' · ')}
        </span>
      </div>
      {payload.charts.length > 0 && (
        <ChartDashboardSparkPreview payload={payload} />
      )}
      {onOpen !== undefined && (
        <span className="text-xs text-muted-foreground">
          Click to open dashboard
        </span>
      )}
    </button>
  );
};

export function isDashboardArtifactType(artifactType: string): boolean {
  return artifactType === CHART_DASHBOARD_ARTIFACT_TYPE;
}

export function parseDashboardPayload(
  payload: unknown
): ChartDashboardPayload | null {
  return isChartDashboardPayload(payload) ? payload : null;
}
